import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { clsx } from 'clsx';
import { Product, ProductCategory } from 'shared/@generated/models';
import { Paginator, Props } from 'shared/@types';
import { H2 } from 'shared/components/title';
import { ShopProductCard } from 'shared/components/shop/product-card';
import { ShopProductCategoryCard } from 'shared/components/shop/product-category-card';
import { ShopHomeHero } from 'shared/components/shop/shop-home-hero';
import { ShopHomeTractiveBanner } from 'shared/components/shop/shop-home-tractive-banner';
import { PageBuilder } from 'shared/library/page-builder';
import { apiRoute } from 'shared/library/api-route';
import { ShopData } from 'shared/state/shop';
import { Pagination } from 'shared/components/pagination';

export interface ShopHomeScreenProps extends Props {
  categories: ProductCategory[];
  products: Paginator<Product>;
  shopData?: ShopData;
}

function ShopHomeScreen({ categories, products }: ShopHomeScreenProps) {
  const { t } = useTranslation('screens');

  return (
    <View className={clsx('p-3 md:p-6 md:py-8', Platform.OS !== 'web' && 'min-h-0 flex-1')}>
      <View
        className={clsx(
          'mx-auto w-full max-w-screen-xl',
          Platform.OS !== 'web' && 'min-h-0 flex-1',
        )}
      >
        <Pagination
          paginator={products}
          renderItem={(item) => <ShopProductCard key={item.id} product={item} />}
          ListHeader={() => (
            <>
              <ShopHomeHero
                className={Platform.OS === 'web' ? '-mx-3 -mt-3 rounded-none md:mx-0' : '-mt-3'}
              />
              <ShopHomeTractiveBanner />
              <H2 title={t('shop.shop_home.popular_products')} marginBottom={false} />
            </>
          )}
          ListFooter={() => (
            <>
              <H2 title={t('shop.shop_home.categories')} />
              <View
                className={clsx(
                  'gap-3 md:gap-6',
                  Platform.OS === 'web' && 'flex-cols-1 md:flex-cols-4 flex-row',
                )}
              >
                {categories.map((category) => (
                  <ShopProductCategoryCard key={category.id} category={category} />
                ))}
              </View>
            </>
          )}
          showPagination={false}
          cols={2}
          lgCols={4}
          xlCols={5}
        />
      </View>
    </View>
  );
}

ShopHomeScreen.pageBuilder = new PageBuilder<ShopHomeScreenProps>()
  .includeShopData()
  .transformProps(({ props }) => ({
    ...props,
    categories: props.shopData?.productCategories ?? [],
  }))
  .request(({ req }) =>
    req<{ products: Paginator<Product> }>(apiRoute('products.index'), {
      sort_by: 'popular',
    }).then((response) => ({
      products: response.products,
    })),
  );

export { ShopHomeScreen };
